// skinToneEngine.js
//
// Skin tone is a genuinely different problem from general exposure and
// color, and it was genuinely missing from this site — every other
// setting here answers "is this technically correct" and skin tone
// answers "does this look like the person actually looks."
//
// -----------------------------------------------------------------------
// WHAT THIS DOES AND DOESN'T CLAIM
//
// Some cameras expose a literal "Skin Detail" or "Skin Tone" control in
// their menu (several Sony and Panasonic bodies do). cameras.json does not
// currently record that field per camera — adding it honestly would mean
// checking each camera's actual menu, which nobody has done yet. Rather
// than invent a plausible-looking value, this module derives guidance from
// fields that ARE on file: the picture profile, the sharpness/contrast/
// saturation controls, and the lighting's color temperature. That's a
// smaller claim than "this camera's Skin Detail control does X", and it's
// the honest one until the per-camera field gets filled in.
//
// If you later confirm a camera's actual skin-tone control, add it to
// cameras.capabilities as `skinTone: { adjustable, type, range, verified }`
// following the same shape as sharpness/contrast/saturation, and extend
// buildSkinTone() below to read it — the derived fallback stays for every
// camera that field hasn't been checked on yet.
// -----------------------------------------------------------------------

function warmthNote(lighting) {
  if (lighting.wbKelvin == null) {
    return "Mixed light is the hardest condition for skin tone specifically, a single white balance can't correct for two color casts on the same face at once. Match to whichever source is actually lighting the skin, not the background.";
  }
  if (lighting.wbKelvin <= 4000) {
    return `${lighting.name} skews warm (${lighting.wbKelvin}K). Left uncorrected that reads as healthy on camera far more often than it reads as wrong, so err toward under-correcting rather than fully neutralizing it.`;
  }
  if (lighting.wbKelvin >= 6500) {
    return `${lighting.name} skews cool (${lighting.wbKelvin}K), and cool light is the one that makes skin look unwell on camera. Warm the white balance slightly past neutral rather than leaving it exactly correct, a small deliberate warm bias reads as intentional; cool skin reads as a mistake.`;
  }
  return `${lighting.name} sits close to neutral (${lighting.wbKelvin}K), which is the easiest condition for skin tone, the white balance in the main recipe needs no adjustment for this specifically.`;
}

function sharpnessNote(camera, requiresGrading) {
  const style = camera.capabilities.imageStyle;
  if (!style || !style.sharpness) {
    return "This camera's sharpness control isn't confirmed in our database, so there's no specific adjustment to recommend beyond what the main recipe already sets.";
  }
  if (style.sharpness.adjustable === false) {
    return `${camera.name} doesn't expose a separate sharpness control, so there's nothing to pull back for skin specifically, whatever the sensor and processing produce is what you get.`;
  }
  if (requiresGrading) {
    return "The main recipe already sets sharpness to its minimum for grading, which happens to be correct for skin too, in-camera sharpening exaggerates texture and pores in a way that's hard to undo afterward, so leaving it low serves both goals at once.";
  }
  return "A touch below the camera's default sharpness reads as more flattering on skin than the default does, at the cost of very slightly softer edges elsewhere in the frame. Worth trying if faces are close to the lens.";
}

function profileNote(settings) {
  if (settings.requiresGrading) {
    return `Shooting ${settings.profile} means skin tone isn't fixed at capture, it's set in the grade. Get exposure and white balance close in camera, but the actual tone comes from the grade, and it's worth spending real time on a face-heavy edit rather than applying a one-click LUT and moving on.`;
  }
  return `${settings.profile} bakes in color at capture, so what the white balance and exposure settings in the main recipe produce is close to the final look. Small mistakes here are harder to fix afterward than they would be on a flat profile.`;
}

/**
 * @param {object} camera
 * @param {object} lighting
 * @param {object} recipeSettings - the `settings` object from generateRecipe()
 * @param {object} scenario
 */
export function generateSkinToneGuidance(camera, lighting, recipeSettings, scenario) {
  const facePriority = (scenario.priority || []).includes("face");

  return {
    relevant: facePriority,
    warmth: warmthNote(lighting),
    sharpness: sharpnessNote(camera, recipeSettings.requiresGrading),
    profile: profileNote(recipeSettings),
    summary: facePriority
      ? `${scenario.name} puts a face in frame for most of the shot, so skin tone is worth the extra attention below rather than trusting the general exposure and white balance settings alone.`
      : `${scenario.name} doesn't center a face for most of the shot, so the general white balance and exposure settings above are enough, the notes below are here for reference, not because this shot specifically needs them.`,
  };
}

/**
 * Reference-page version: not tied to one scenario, so it shows the
 * warmth guidance across all twelve lighting conditions plus the
 * sharpness/profile notes for this camera specifically.
 */
export function generateSkinToneReference(camera, lightingConditions) {
  const rows = lightingConditions.map((l) => [l.name, warmthNote(l).split(".")[0] + "."]);
  return {
    tables: [{ caption: "White balance bias for skin, by lighting condition", columns: ["Lighting", "Guidance"], rows }],
    sharpness: sharpnessNote(camera, false),
    sharpnessGraded: sharpnessNote(camera, true),
  };
}
